import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ORGANIZATION_ADMIN, PROJECT_ADMIN, PROJECT_MONITOR } from 'app/types/authority';
import { StateStorageService } from './state-storage.service';
import { AccountService } from './account.service';



@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate, CanActivateChild {
  constructor(
    private router: Router,
    private stateStorage: StateStorageService,
    private accountService: AccountService
  ) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    this.setStateStorage(route);
    return this.authorize(route);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    this.setStateStorage(route);
    return this.authorize(route);
  }

  setStateStorage(route: ActivatedRouteSnapshot) {
    if (route !== undefined) {
      this.stateStorage.storeDestinationState({
        path: route.url,
        queryParams: route.queryParams,
        authorities: route.data?.authorities,
      });
    } else {
      this.stateStorage.resetDestinationState();
    }
  }

  authorize(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.accountService.identity().pipe(
      map((account) => {
        const authorities = route.routeConfig!.data?.authorities;
        const hasAnyAuthority = this.accountService.accountHasAnyAuthority(account!, authorities);

        if (!hasAnyAuthority) {
          if (this.accountService.accountHasAnyAuthority(account!, [ORGANIZATION_ADMIN])) {
            return this.router.createUrlTree(['/core', 'organization', 'study']);
          }
          if (this.accountService.accountHasAnyAuthority(account!, [PROJECT_ADMIN, PROJECT_MONITOR])) {
            return this.router.createUrlTree(['/core', 'study', 'list']);
          }
          return this.handleUnauthenticated();
        }
        return hasAnyAuthority;
      }),
    );
  }

  redirectBeforeUnauthenticated(): Promise<boolean> {
    const previousState = this.stateStorage.getPreviousState();

    if (previousState) {
      this.stateStorage.resetPreviousState();
      return this.router.navigate(previousState.path ? previousState.path.map(p => p.path ? p.path : p.parameters) : ['.'],
        {
          queryParams: previousState.queryParams
        }
      );
    }
    return Promise.resolve(false);
  }

  private handleUnauthenticated(): UrlTree {
    // user is not authenticated. Show the state they wanted before you
    // send them to the login service, so you can return them when you're done
    const destState = this.stateStorage.getDestinationState();
    this.stateStorage.storePreviousState(destState);

    // now, send them to the sign in state so they can log in
    return this.router.createUrlTree(['accessdenied']);
  }
}
